import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppStateInterface } from 'app/core/app-config/state/models';
import { Observable, BehaviorSubject } from 'rxjs';

import { CustomFieldInterface } from '../models/custom-field.model';
import { LicenseDataInterface } from '../models/license-data.model';
import { SiteEmailConfigInterface } from '../models/site-email-config.model';
import { SiteSettingInterface } from '../models/site-setting.model';
import { SSOStatusInterface } from '../models/active.model';
import { SSOMetadataInterface } from '../models/sso-metadata.model';
import { MessageBannerInterface, MessageInterface, MessageTextInterface } from '../models/message-banner.model';
import { ErrorsSSOInterface, ErrorSSOSingleInterface } from '../models/error-message';
import { LoadCustomFields, UpdateCustomFields } from '../state/actions/custom-fields.action';
import { LoadEmailConfig, UpdateEmailConfig } from '../state/actions/email-config.action';
import { LoadSiteLicense, UpdateSiteLicense } from '../state/actions/site-license.action';
import { LoadSiteSettings,
  UpdateSiteSetting,
  UpdateBannerCheck,
  LoadBanner,
  LoadTextBanner,
  UpdateBannerMessage,
  LoadErrorSSOMessage,
  UpdateErrorSSOMessage,
  LoadUserProps,
  LoadErrorSSOSingleMessage,
  UpdateSsoSetting,
  CheckSsoKey,
  UpdateSsoKey,
  LoadSSOMetadata,
 } from '../state/actions/site-settings.action';

import { BackupTranslationInterface } from '../models/backup-translation.model';
import { PasswordSettingsInterface } from '../models/password-settings.model';
import { SiteSupportedLanguageInterface } from '../models/site-supported-language.model';
import { UserSessionSettingsInterface } from '../models/usersession-settings.model';
import { LoadBackupTranslate, UpdateBackupTranslate } from '../state/actions/backup-translation.action';
import { LoadPasswordSettings, UpdatePasswordSettings } from '../state/actions/password-settings.action';
import { LoadSiteSupportedLanguage, UpdateSiteSupportedLanguage } from '../state/actions/site-supported-language.action';
import { LoadUserSessionSettings, UpdateUserSessionSettings } from '../state/actions/usersession-settings.action';

@Injectable({
  providedIn: 'root',
})
export class SiteSettingsService {
  constructor(private readonly _store: Store<AppStateInterface>) {}

  public loadSiteSettings(): Observable<SiteSettingInterface[]> {
    this._store.dispatch(new LoadSiteSettings());

    return this._store.select(({ siteSettings }: AppStateInterface) => siteSettings ? siteSettings.settingsCollection : []);
  }

  public loadUsersProps(): Observable<SSOStatusInterface> {
    this._store.dispatch(new LoadUserProps());

    return this._store.select(({ siteSettings }: AppStateInterface) => siteSettings ? siteSettings.userProps : undefined);
  }

  public loadSsoMetadata(): Observable<SSOMetadataInterface> {
    this._store.dispatch(new LoadSSOMetadata());

    return this._store.select(({ siteSettings }: AppStateInterface) => siteSettings ? siteSettings.ssoMetadata : undefined);
  }

  public getSiteSetting(settingToGet: string): Observable<SiteSettingInterface> {
    this._store.dispatch(new LoadSiteSettings());

    return this._store.select(({ siteSettings }: AppStateInterface) => {
      if(siteSettings && siteSettings.settingsCollection && siteSettings.settingsCollection.length > 0) {
        return siteSettings.settingsCollection.filter((setting: SiteSettingInterface) => setting.setting === settingToGet)[0];
      }

      return undefined;
    });
  }

  public checkSsoKey(key: string): Observable<boolean> {
    this._store.dispatch(new CheckSsoKey(key));

    return this._store.select(( {siteSettings} : AppStateInterface) => siteSettings ? siteSettings.ssoKeyValid : undefined);
  }

  public updateSsoKey(key: string): void {
    this._store.dispatch(new UpdateSsoKey(key));

    // return this._store.select(({ siteSettings }: AppStateInterface) => siteSettings ? siteSettings.licenseUpdateResponse : undefined);
  }

  public updateSsoSetting(setting: any): void {
    this._store.dispatch(new UpdateSsoSetting(setting));

    // return this._store.select(( {siteSettings} : AppStateInterface) => siteSettings ? siteSettings.ssoSetting : undefined);
  }

  public updateBanner(active: boolean): void {
    this._store.dispatch(new UpdateBannerCheck(active));

    // return this._store.select(({ siteSettings }: AppStateInterface) => siteSettings ? siteSettings.licenseUpdateResponse : undefined);
  }

  public updateBannerMessage(message: MessageInterface): Observable<MessageInterface> {
    this._store.dispatch(new UpdateBannerMessage(message));

    return this._store.select(({ siteSettings }: AppStateInterface) => siteSettings ? siteSettings.singleMessageBanner : undefined);
  }

  public loadBanner(): Observable<MessageBannerInterface> {
    this._store.dispatch(new LoadBanner());

    return this._store.select(({ siteSettings }: AppStateInterface) => siteSettings? siteSettings.messageBanner : undefined);
  }

  public loadTextBanner(): Observable<MessageTextInterface> {
    this._store.dispatch(new LoadTextBanner());

    return this._store.select(({ siteSettings }: AppStateInterface) => siteSettings? siteSettings.messageTextBanner : undefined);
  }

  public loadErrorSSOMessage(): Observable<ErrorsSSOInterface[]> {
    this._store.dispatch(new LoadErrorSSOMessage());

    return this._store.select(({ siteSettings }: AppStateInterface) => siteSettings? siteSettings.errorsSSO : undefined);
  }

  public updateErrorSSOMessage(message: ErrorSSOSingleInterface): Observable<ErrorSSOSingleInterface> {
    this._store.dispatch(new UpdateErrorSSOMessage(message));

    return this._store.select(({ siteSettings }: AppStateInterface) => siteSettings? siteSettings.errorsSingleSSO : undefined);
  }
  public loadErrorSSOSingleMessage(id: number): Observable<ErrorSSOSingleInterface> {
    this._store.dispatch(new LoadErrorSSOSingleMessage(id));

    return this._store.select(({ siteSettings }: AppStateInterface) => siteSettings? siteSettings.errorsSingleSSO : undefined);
  }

  public updateSetting(setting: SiteSettingInterface): void {
    this._store.dispatch(new UpdateSiteSetting(setting));
  }

  public loadCustomFields(): Observable<CustomFieldInterface[]> {
    this._store.dispatch(new LoadCustomFields());

    return this._store.select(({ siteSettings }: AppStateInterface) => siteSettings ? siteSettings.customFieldsCollection : undefined);
  }

  public updateCustomFields(customFieldData: CustomFieldInterface[]): void {
    this._store.dispatch(new UpdateCustomFields(customFieldData));
  }

  public loadEmailConfig(): Observable<SiteEmailConfigInterface> {
    this._store.dispatch(new LoadEmailConfig());

    return this._store.select(({ siteSettings }: AppStateInterface) => siteSettings ? siteSettings.siteEmailConfig : undefined);
  }

  public updateEmailConfig(emailConfigData: SiteEmailConfigInterface): void {
    this._store.dispatch(new UpdateEmailConfig(emailConfigData));
  }

  public loadSiteLicesnse(): Observable<LicenseDataInterface> {
    this._store.dispatch(new LoadSiteLicense());

    return this._store.select(({ siteSettings }: AppStateInterface) => siteSettings ? siteSettings.siteLicenseData : undefined);
  }

  public updateSiteLicense(newLicenseKey: string): Observable<string> {
    this._store.dispatch(new UpdateSiteLicense(newLicenseKey));

    return this._store.select(({ siteSettings }: AppStateInterface) => siteSettings ? siteSettings.licenseUpdateResponse : undefined);
  }
  public loadSiteSupportedLanguage(): Observable<SiteSupportedLanguageInterface[]> {
    this._store.dispatch(new LoadSiteSupportedLanguage());

    // tslint:disable-next-line:max-line-length
    return this._store.select(({ siteSettings }: AppStateInterface) => siteSettings ? siteSettings.siteSupportedLanguageCollection : undefined);
  }

  public updateSiteSupportedLanguage(languageData: SiteSupportedLanguageInterface[]): void {
    this._store.dispatch(new UpdateSiteSupportedLanguage(languageData));
  }

  public loadBackupTranslate(): Observable<BackupTranslationInterface> {
    this._store.dispatch(new LoadBackupTranslate());

    // tslint:disable-next-line:max-line-length
    return this._store.select(({ siteSettings }: AppStateInterface) => siteSettings ? siteSettings.backupTranslationResponse : undefined);
  }

  public updateBackupTranslate(languageData: BackupTranslationInterface[]): void {
    this._store.dispatch(new UpdateBackupTranslate(languageData));
  }

  public loadPasswordSettings(userId:number): Observable<PasswordSettingsInterface> {
    this._store.dispatch(new LoadPasswordSettings(userId));

    return this._store.select(({ siteSettings }: AppStateInterface) => siteSettings ? siteSettings.passwordSettingsResponse : undefined);
  }
  public updatePasswordSettings(passwordData: PasswordSettingsInterface): void {
    this._store.dispatch(new UpdatePasswordSettings(passwordData));
  }
  public loadUserSessionSettings(userId:number): Observable<UserSessionSettingsInterface> {
    this._store.dispatch(new LoadUserSessionSettings(userId));

    return this._store.select(({ siteSettings }: AppStateInterface) => siteSettings ? siteSettings.userSessionSettingsResponse : undefined);
  }
  public updateUserSessionSettings(usersessionData: UserSessionSettingsInterface): void {

    this._store.dispatch(new UpdateUserSessionSettings(usersessionData));
  }
}

export class SiteSettingLanguageService {
  public readonly siteSettingSupportedLanguage$: BehaviorSubject<SiteSupportedLanguageInterface[]>;
  constructor() {
    this.siteSettingSupportedLanguage$ = new BehaviorSubject<SiteSupportedLanguageInterface[]>(undefined);
  }
  public get ssLObservable() {
    return this.siteSettingSupportedLanguage$.asObservable();
  }

  public setLangs(sssL:any) {
    this.siteSettingSupportedLanguage$.next(sssL);
   }
   public get() {
     return this.siteSettingSupportedLanguage$;
   }

}
